<script setup lang="ts">
import { computed } from 'vue';

// Props definition
const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  totalPages: {
    type: Number,
    default: 1
  },
  showFirstLast: {
    type: Boolean,
    default: true
  }
});

// Emit events
const emit = defineEmits(['pageChange']);

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= props.totalPages) {
    emit('pageChange', page);
  }
};

const goToFirstPage = () => goToPage(1);
const goToPrevPage = () => goToPage(props.currentPage - 1);
const goToNextPage = () => goToPage(props.currentPage + 1);
const goToLastPage = () => goToPage(props.totalPages);

// Computed properties
const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === props.totalPages);
</script>

<template>
  <div class="rpg-scroll">
    <button
      v-if="showFirstLast"
      class="scroll-button first-button"
      :disabled="isFirstPage"
      @click="goToFirstPage"
      title="First Page"
    >
      «
    </button>

    <button
      class="scroll-button prev-button"
      :disabled="isFirstPage"
      @click="goToPrevPage"
      title="Previous Page"
    >
      ‹
    </button>

    <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>

    <button
      class="scroll-button next-button"
      :disabled="isLastPage"
      @click="goToNextPage"
      title="Next Page"
    >
      ›
    </button>

    <button
      v-if="showFirstLast"
      class="scroll-button last-button"
      :disabled="isLastPage"
      @click="goToLastPage"
      title="Last Page"
    >
      »
    </button>
  </div>
</template>

<style scoped>
.rpg-scroll {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
}

/* Matches the HUD's readout controls: gold on a dark slab, hairline rails. */
.scroll-button {
  background: rgba(13, 13, 13, 0.82);
  color: rgba(232, 238, 245, 0.42);
  border: 1px solid rgba(250, 218, 149, 0.18);
  border-radius: 2px;
  padding: 0.15rem 0.4rem;
  cursor: pointer;
  font-size: 0.8rem;
  line-height: 1;
  min-width: 1.5rem;
  text-align: center;
  transition: color 0.12s ease, border-color 0.12s ease;
}

.scroll-button:hover:not(:disabled) {
  color: #fada95;
  border-color: rgba(250, 218, 149, 0.45);
}

.scroll-button:disabled {
  opacity: 0.3;
  cursor: default;
}

.scroll-button:focus-visible {
  outline: 1px solid #fada95;
  outline-offset: 1px;
}

.page-indicator {
  margin: 0 0.5rem;
  min-width: 3rem;
  text-align: center;
  font-family: ui-monospace, 'IBM Plex Mono', 'SF Mono', Menlo, monospace;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgba(232, 238, 245, 0.42);
}
</style>